import React, { useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "@/contexts";
import { SiteLayout, AppLayout } from "@/layouts";
import RequireAuth from "./RequireAuth";
import {
  // Core / Entry
  IndexScreen,
  NotFoundScreen,

  // Auth Flow
  LoginScreen,
  ForgotPasswordScreen,
  RegisterScreen,
  CompleteRegisterScreen,
  ActionScreen,
  RegisterSuccessScreen,
  RegisterErrorScreen,

  // Main Content
  FeedScreen,
  FeedUserScreen,
  SearchScreen,
  MessagesScreen,
  InterestsScreen,

  // User Profile
  ProfileScreen,
  ProfileEditScreen,
  ProfileConfigScreen,

  // Info & Support
  TrueConnectionsScreen,
  CompatibilityAffinitiesScreen,
  ExclusiveRelationshipsScreen,
  PlansScreen,
  PaymentsScreen,
  IndicationsScreen,
  CheckoutScreen,
  HelpCenterScreen,
  AboutUsScreen,
  HowItWorksScreen,
  PrivacyPolicyScreen,
  TermsScreen,
  SecurityScreen,
  VerificationScreen,
  GuidelinesScreen,
} from "@/screens";

const RootRoute = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get("mode");
  const { user, loading } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/inicio", { replace: true });
    }
  }, [user, loading, navigate]);

  if (mode === "resetPassword" || mode === "verifyEmail") {
    return <ActionScreen />;
  }

  if (loading) return null;

  return <IndexScreen />;
};

const _Routes = () => {
  const location = useLocation();
  const { loading } = useUser();

  useEffect(() => {
    if (window.location.hostname === "localhost") {
      const path = location.pathname;

      const routeMap: { [key: string]: string } = {
        "/": "IndexScreen",
        "/inicio": "FeedScreen",
        "/mensagens": "MessagesScreen",
        "/buscar": "SearchScreen",
        "/interesses": "InterestsScreen",
        "/meu-perfil": "ProfileScreen",
        "/editar-perfil": "ProfileEditScreen",
        "/configuracoes": "ProfileConfigScreen",
        "/planos": "PlansScreen",
        "/pagamentos": "PaymentsScreen",
        "/indicacoes": "IndicationsScreen",
        "/cadastro": "RegisterScreen",
        "/login": "LoginScreen",
        "/esqueci-senha": "ForgotPasswordScreen",
        "/como-funciona": "HowItWorksScreen",
        "/sobre-nos": "AboutUsScreen",
        "/termos-e-condicoes": "TermsScreen",
        "/politica-de-privacidade": "PrivacyPolicyScreen",
        "/seguranca": "SecurityScreen",
        "/verificacao": "VerificationScreen",
        "/diretrizes": "GuidelinesScreen",
        "/conexoes-verdadeiras": "TrueConnectionsScreen",
        "/compatibilidade-e-afinidades": "CompatibilityAffinitiesScreen",
        "/relacionamentos-exclusivos": "ExclusiveRelationshipsScreen",
      };

      let screenName = routeMap[path];

      if (!screenName) {
        if (path.startsWith("/perfil/")) screenName = "FeedUserScreen";
        if (path.startsWith("/checkout/")) screenName = "CheckoutScreen";
        if (path === "/404") screenName = "NotFoundScreen";
      }

      console.log(
        `%c 📍 TELA ATUAL: ${screenName || "Rota não mapeada"} %c (${path})`,
        "color: #fff; background: #9e764e; font-weight: bold; padding: 2px 5px; border-radius: 3px;",
        "color: #777;",
      );
    }
  }, [location]);

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      {!loading && (
        <Routes>
          {/* --- Rotas Públicas (Site Institucional / Landing) --- */}
          <Route element={<SiteLayout />}>
            <Route path="/" element={<RootRoute />} />

            {/* Institucional / Explicação */}
            <Route path="/como-funciona" element={<HowItWorksScreen />} />
            <Route path="/sobre-nos" element={<AboutUsScreen />} />

            <Route
              path="/conexoes-verdadeiras"
              element={<TrueConnectionsScreen />}
            />
            <Route
              path="/compatibilidade-e-afinidades"
              element={<CompatibilityAffinitiesScreen />}
            />
            <Route
              path="/relacionamentos-exclusivos"
              element={<ExclusiveRelationshipsScreen />}
            />

            {/* Fluxo de Cadastro */}
            <Route path="/cadastro" element={<RegisterScreen />} />
            <Route
              path="/completar-cadastro"
              element={<CompleteRegisterScreen />}
            />
            <Route
              path="/cadastro/sucesso"
              element={<RegisterSuccessScreen />}
            />
            <Route path="/cadastro/erro" element={<RegisterErrorScreen />} />

            {/* Fluxo de Autenticação */}
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/esqueci-senha" element={<ForgotPasswordScreen />} />
            <Route path="/auth/acao" element={<ActionScreen />} />
            <Route
              path="/auth/redefinir-senha"
              element={<ActionScreen defaultMode="resetPassword" />}
            />
            <Route
              path="/auth/verificar-email"
              element={<ActionScreen defaultMode="verifyEmail" />}
            />

            {/* Legal / Suporte */}
            <Route
              path="/politica-de-privacidade"
              element={<PrivacyPolicyScreen />}
            />
            <Route path="/termos-e-condicoes" element={<TermsScreen />} />
            <Route path="/central-de-ajuda" element={<HelpCenterScreen />} />
            <Route path="/seguranca" element={<SecurityScreen />} />
            <Route path="/verificacao" element={<VerificationScreen />} />
            <Route path="/diretrizes" element={<GuidelinesScreen />} />

            <Route path="/404" element={<NotFoundScreen />} />
          </Route>

          {/* --- Rotas Privadas (App / Logado) --- */}
          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route path="/inicio" element={<FeedScreen />} />
              <Route path="/perfil/:username" element={<FeedUserScreen />} />
              <Route path="/mensagens" element={<MessagesScreen />} />
              <Route path="/buscar" element={<SearchScreen />} />
              <Route path="/interesses" element={<InterestsScreen />} />

              <Route path="/meu-perfil" element={<ProfileScreen />} />
              <Route path="/editar-perfil" element={<ProfileEditScreen />} />
              <Route path="/configuracoes" element={<ProfileConfigScreen />} />

              <Route path="/planos" element={<PlansScreen />} />
              <Route path="/checkout/:planId" element={<CheckoutScreen />} />
              <Route path="/pagamentos" element={<PaymentsScreen />} />
              <Route path="/indicacoes" element={<IndicationsScreen />} />
            </Route>
          </Route>

          {/* Fallback Global */}
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
      )}
    </>
  );
};

export default _Routes;
